import { useState } from 'react';
import { toast } from 'react-toastify';
import type { InstallView, ListingDetail, ReviewView } from '@dunea/shared';
import { apiListListingReviews, apiWriteReview } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import {
  apiErrorMessage,
  REVIEW_BODY_MAX,
  REVIEW_TITLE_MAX,
  reviewDraftError,
  reviewEligibility,
} from './marketplace-model';
import { Stars } from './parts';

/** Public reviews of a listing, and "Write a review" for owners who installed it. */
export function ListingReviews({ listing, install }: { listing: ListingDetail; install: InstallView | null }) {
  const [page, setPage] = useState(1);
  const reviews = useResource(() => apiListListingReviews(listing.slug, page), `${listing.slug}:${page}`);
  const [extra, setExtra] = useState<ReviewView[]>([]);
  const [writing, setWriting] = useState(false);
  const eligibility = reviewEligibility(install, listing.kind);

  const items = [...extra, ...(reviews.data?.items ?? [])];

  return (
    <Section
      title="Reviews"
      icon="i-ph:star"
      aside={
        eligibility.canReview && !writing ? (
          <button type="button" onClick={() => setWriting(true)} className={BTN_GHOST}>
            <div className="i-ph:pencil-simple text-[13px]" />
            Write a review
          </button>
        ) : null
      }
    >
      {!eligibility.canReview && eligibility.message && <p className="text-[11px] text-muted">{eligibility.message}</p>}

      {writing && (
        <ReviewForm
          slug={listing.slug}
          onCancel={() => setWriting(false)}
          onPosted={(review) => {
            setExtra((prev) => [review, ...prev]);
            setWriting(false);
          }}
        />
      )}

      {reviews.error ? (
        <LoadError message="Could not load reviews." onRetry={() => void reviews.reload()} />
      ) : !reviews.data ? (
        <SkeletonCard />
      ) : items.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-5 text-center text-[12px] text-muted')}>No reviews yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <ReviewItem key={r.id} review={r} />
          ))}
        </div>
      )}

      {reviews.data && (page > 1 || reviews.data.hasMore) && (
        <div className="flex justify-between gap-2 pt-1">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={BTN_GHOST}>
            Newer
          </button>
          <button
            type="button"
            disabled={!reviews.data.hasMore}
            onClick={() => setPage((p) => p + 1)}
            className={BTN_GHOST}
          >
            Older
          </button>
        </div>
      )}
    </Section>
  );
}

function ReviewItem({ review }: { review: ReviewView }) {
  return (
    <article className={classNames(CARD, 'space-y-1.5 p-3.5')}>
      <div className="flex items-center justify-between gap-2">
        <Stars rating={review.rating} />
        <span className="text-[10.5px] text-muted">{formatDate(review.createdAt)}</span>
      </div>
      {review.title && <h5 className="text-[12.5px] font-medium text-primary">{review.title}</h5>}
      {review.body && <p className="whitespace-pre-line text-[12px] leading-relaxed text-secondary">{review.body}</p>}
      <div className="flex items-center gap-1.5 text-[11px] text-muted">
        <span>{review.authorName}</span>
        {review.verifiedInstall && (
          <span className="inline-flex items-center gap-0.5 text-success">
            <span className="i-ph:check-circle text-[11px]" />
            Installed
          </span>
        )}
      </div>
      {review.developerReply && (
        <div className="mt-1 rounded-lg border border-hairline bg-sunken px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] text-muted">Developer reply</div>
          <p className="mt-0.5 whitespace-pre-line text-[12px] leading-relaxed text-secondary">
            {review.developerReply}
          </p>
        </div>
      )}
    </article>
  );
}

function ReviewForm({
  slug,
  onCancel,
  onPosted,
}: {
  slug: string;
  onCancel: () => void;
  onPosted: (review: ReviewView) => void;
}) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const problem = reviewDraftError({ rating, title, body });

    if (problem) {
      setError(problem);
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const review = await apiWriteReview(slug, {
        rating,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(body.trim() ? { body: body.trim() } : {}),
      });
      toast.success('Thanks for the review.');
      onPosted(review);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not post your review.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className={classNames(CARD, 'space-y-3 p-4')}
    >
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            onClick={() => setRating(n)}
            className="ui-btn-icon"
          >
            <span
              className={classNames(
                n <= rating ? 'i-ph:star-fill text-warning' : 'i-ph:star text-muted',
                'text-[16px]',
              )}
            />
          </button>
        ))}
      </div>
      <Field label="Title (optional)">
        <input
          value={title}
          maxLength={REVIEW_TITLE_MAX}
          onChange={(e) => setTitle(e.target.value)}
          className="ui-input"
        />
      </Field>
      <Field label="Review (optional)">
        <textarea
          value={body}
          maxLength={REVIEW_BODY_MAX}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="ui-input resize-y"
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          Post review
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
